<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';

const store = useStore();
const globalConfig = computed(() => store.getters['globalConfig/get']);
</script>

<template>
  <footer
    class="bg-surface-container-highest dark:bg-deep-navy border-t border-outline-variant dark:border-primary-container py-8 text-left"
  >
    <div
      class="grid grid-cols-1 md:grid-cols-4 gap-gutter px-margin-desktop max-w-container-max mx-auto"
    >
      <!-- Logo and Description -->
      <div class="space-y-4">
        <router-link
          to="/"
          class="block"
        >
          <img
            src="/brand-assets/logo_horizontal.png"
            alt="OS DESK"
            class="h-11 w-auto dark:hidden"
          />
          <img
            src="/brand-assets/logo_horizontal_dark.png"
            alt="OS DESK"
            class="h-11 w-auto hidden dark:block"
          />
        </router-link>
        <p
          class="font-caption text-caption text-on-surface-variant dark:text-outline-variant max-w-[200px]"
        >
          The AI-native helpdesk for the modern enterprise architect.
        </p>
      </div>

      <!-- Product Links -->
      <div>
        <h4
          class="font-label-md text-label-md text-primary dark:text-secondary-fixed mb-4 font-bold"
        >
          Product
        </h4>
        <div
          class="flex flex-col gap-2 font-caption text-caption text-on-surface-variant dark:text-outline-variant"
        >
          <router-link
            class="hover:text-secondary transition-colors"
            to="/features"
          >
            Features
          </router-link>
          <router-link
            class="hover:text-secondary transition-colors"
            to="/inbox"
          >
            Omnichannel Inbox
          </router-link>
          <router-link
            class="hover:text-secondary transition-colors"
            to="/security"
          >
            Security
          </router-link>
        </div>
      </div>

      <!-- Legal Links -->
      <div>
        <h4
          class="font-label-md text-label-md text-primary dark:text-secondary-fixed mb-4 font-bold"
        >
          Legal
        </h4>
        <div
          class="flex flex-col gap-2 font-caption text-caption text-on-surface-variant dark:text-outline-variant"
        >
          <router-link
            class="hover:text-secondary transition-colors"
            to="/privacy"
          >
            Privacy Policy
          </router-link>
          <router-link
            class="hover:text-secondary transition-colors"
            to="/terms"
          >
            Terms of Service
          </router-link>
          <router-link
            class="hover:text-secondary transition-colors"
            to="/cookie-policy"
          >
            Cookie Policy
          </router-link>
          <router-link class="hover:text-secondary transition-colors" to="/dpa">
            Data Processing Agreement
          </router-link>
        </div>
      </div>

      <!-- Connect Links -->
      <div>
        <h4
          class="font-label-md text-label-md text-primary dark:text-secondary-fixed mb-4 font-bold"
        >
          Connect
        </h4>
        <div
          class="flex flex-col gap-2 font-caption text-caption text-on-surface-variant dark:text-outline-variant"
        >
          <router-link
            class="hover:text-secondary transition-colors"
            to="/contact"
          >
            Contact
          </router-link>
          <router-link
            class="hover:text-secondary transition-colors"
            to="/help-center"
          >
            Support
          </router-link>
          <router-link
            class="hover:text-secondary transition-colors"
            to="/about"
          >
            About Us
          </router-link>
        </div>
      </div>
    </div>

    <!-- Bottom Bar with Social Media Icons -->
    <div
      class="mt-8 px-margin-desktop max-w-container-max mx-auto pt-6 border-t border-outline-variant/30 flex flex-col md:flex-row justify-between items-center gap-4"
    >
      <p
        class="font-caption text-caption text-on-surface-variant dark:text-outline-variant"
      >
        © 2024 {{ globalConfig.installationName || 'OS DESK' }} AI. All rights
        reserved.
      </p>
      <div
        class="flex items-center gap-4 text-on-surface-variant dark:text-outline-variant"
      >
        <a
          href="https://twitter.com"
          target="_blank"
          class="hover:text-primary transition-colors duration-200"
          aria-label="X / Twitter"
        >
          <svg class="w-5 h-5 fill-current" viewBox="0 0 24 24">
            <path
              d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
            />
          </svg>
        </a>
        <a
          href="https://linkedin.com"
          target="_blank"
          class="hover:text-primary transition-colors duration-200"
          aria-label="LinkedIn"
        >
          <svg class="w-5 h-5 fill-current" viewBox="0 0 24 24">
            <path
              d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0z"
            />
          </svg>
        </a>
        <a
          href="https://github.com"
          target="_blank"
          class="hover:text-primary transition-colors duration-200"
          aria-label="GitHub"
        >
          <svg class="w-5 h-5 fill-current" viewBox="0 0 24 24">
            <path
              d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"
            />
          </svg>
        </a>
      </div>
    </div>
  </footer>
</template>
